import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Package, Shield, Wrench, Truck, Smartphone, HelpCircle, Mail,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqsAPI } from '../services/api';
import { useSEO } from '../hooks/useSEO';

const ICONS = {
  HelpCircle, Package, Shield, Wrench, Truck, Smartphone, Mail,
};

const FAQPage = () => {
  useSEO({
    title: 'Frequently Asked Questions',
    description: 'Answers to common questions about BlueSpark tuning modules — installation, compatibility, warranty, returns, and Bluetooth connectivity.',
    canonical: '/faq',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: 'BlueSpark Australia FAQ',
    },
  });
  const { section } = useParams();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    faqsAPI.getAll()
      .then(setSections)
      .finally(() => setLoading(false));
  }, []);

  const currentSection =
    sections.find((s) => s.id === section) ||
    sections.find((s) => s.id === 'general') ||
    sections[0] ||
    { title: 'FAQ', icon: 'HelpCircle', items: [] };
  const IconComponent = ICONS[currentSection.icon] || HelpCircle;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50">
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-8">
            <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center space-x-3">
              <IconComponent className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl md:text-4xl font-bold">{currentSection.title}</h1>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {loading ? (
            <p className="text-center text-gray-500">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">FAQ Categories</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {sections.map((s) => {
                      const Icon = ICONS[s.icon] || HelpCircle;
                      const active = section === s.id || (!section && s.id === 'general');
                      return (
                        <Link
                          key={s.id}
                          to={`/faq/${s.id}`}
                          className={`flex items-center space-x-2 p-3 rounded-lg transition-colors ${
                            active ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-gray-100'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm">{s.title}</span>
                        </Link>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-3">
                <Card>
                  <CardContent className="p-6">
                    <Accordion type="single" collapsible className="w-full">
                      {(currentSection.items || []).map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`}>
                          <AccordionTrigger className="text-left">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>

                <Card className="mt-6 bg-blue-50 border-blue-200">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-lg font-semibold mb-2">Still have questions?</h3>
                    <p className="text-gray-600 mb-4">
                      Our support team is here to help with any questions about our tuning modules.
                    </p>
                    <Link to="/faq/contact">
                      <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                        Contact Support
                      </button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
